import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { NAV_LINKS, BRAND } from '@/data/content';
import { scrollToId } from '@/lib/smoothScroll';

export const Navbar = ({ visible }) => {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <motion.header
        initial={{ y: -90, opacity: 0 }}
        animate={visible ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 inset-x-0 z-50 px-4 md:px-8 pt-4"
      >
        <nav className="glass rounded-full max-w-6xl mx-auto flex items-center justify-between pl-6 pr-2 py-2">
          <button data-testid="nav-logo" onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center gap-2.5 group">
            <img src="/assets/logo-mark.png" alt="Jupally Avenues" className="h-8 w-auto" />
            <span className="flex items-baseline gap-2">
              <span className="font-clash font-semibold text-lg tracking-tight text-[#1E3A7B] transition-colors duration-300">JUPALLY</span>
              <span className="text-[9px] uppercase tracking-[0.3em] text-brand-gold">Avenues</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="px-4 py-2 text-[13px] text-ink/80 hover:text-ink rounded-full hover:bg-ink/5 transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <a
              data-testid="nav-phone-cta"
              href={`tel:${BRAND.phones[0].replace(/\s/g, '')}`}
              className="hidden md:flex items-center gap-2 bg-brand-gold text-ink text-[13px] font-semibold px-5 py-2.5 rounded-full hover:bg-ink hover:text-white transition-colors duration-300"
            >
              <Phone size={14} /> {BRAND.phones[0]}
            </a>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-ink/5 text-ink"
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 bg-void/95 backdrop-blur-xl flex flex-col justify-center px-8 md:hidden"
          >
            {/* Brand identity at top of mobile menu */}
            <div className="absolute top-6 left-8 flex items-center gap-3">
              <img src="/assets/logo-mark.png" alt="Jupally Avenues" className="h-9 w-auto" />
              <span className="flex items-baseline gap-2">
                <span className="font-clash font-semibold text-lg tracking-tight text-[#1E3A7B]">JUPALLY</span>
                <span className="text-[9px] uppercase tracking-[0.3em] text-brand-gold">Avenues</span>
              </span>
            </div>

            {NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 + i * 0.07 }}
                onClick={() => go(l.id)}
                className="text-left font-clash text-4xl font-medium text-ink py-3 border-b border-ink/10"
              >
                {l.label}
              </motion.button>
            ))}
            <p className="mt-8 text-brand-gold text-sm tracking-widest">{BRAND.phones[0]}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
